import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/landing/primitives";
import { Typewriter } from "@/components/landing/Typewriter";
import { Eyebrow, H2, Shot, ALPHA_URL, VERIFY_URL } from "./shared";
import { ResearchMock, NewsMock } from "./Mocks";

const ROWS = [
  { k: "F3 / Market Pulse", q: "Where is the next breakout coming from?", body: "Pulse scans for unusual volume, shifting buy pressure and price compression, so you see volatility building before the move, not after.", img: "/of/pulse.webp", href: ALPHA_URL + "/screener", cta: "Open TQ Pulse", live: true },
  { k: "F2 / Order Flow", q: "Who is really buying?", body: "Footprint candles, Smart Tape, CVD, the live ladder and tracked Hyperliquid whale positions, all built on real executed trades.", img: "/of/footprint.webp", href: "/order-flow", cta: "Explore order flow", live: true },
  { k: "F4 / Deep Research", q: "Is this coin worth it?", body: "Scorecards on liquidity, holder concentration, supply unlocks, on-chain activity and narrative, so you know if an asset deserves your capital.", el: <ResearchMock />, href: "/research", cta: "See the research lab" },
  { k: "F5 / Breaking News", q: "What just moved price?", body: "Market-moving headlines ranked by impact, with the price reaction measured in real time.", el: <NewsMock />, ticker: true, href: "#access", cta: "Included at launch" },
  { k: "F1 / Trade Setups", q: "What's the trade?", body: "A-grade setups with entry, stop and target from the 24/7 engine. Every one is published to Verify before the outcome is known.", img: "/of/verify.webp", href: VERIFY_URL, cta: "Audit the setups", live: true },
];

const HEADLINES = ["BREAKING · US CPI below consensus · BTC +1.4% in 4m", "BREAKING · Whale moves 12k ETH to exchange · ETH −0.6%", "BREAKING · SOL perp leverage cut · SOL −2.1% in 12m"];

const Row = ({ r, flip }) => (
  <div data-testid={`module-row-${r.k.split(" / ")[1].toLowerCase().replace(/\s+/g, "-")}`} className="grid items-center gap-10 border-t border-white/[0.06] py-16 md:py-20 lg:grid-cols-[1fr_1.45fr] lg:gap-16">
    <Reveal className={flip ? "lg:order-2" : ""}>
      <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#7D8390]">
        <span className="text-[#FFB224]">{r.k.split(" / ")[0]}</span>{r.k.split(" / ")[1]}
        {r.live ? <span className="border border-white/15 px-1.5 py-0.5 text-[9px] text-[#9DA3AE]">Live now</span> : <span className="border border-white/10 px-1.5 py-0.5 text-[9px] text-[#5A6170]">Full terminal</span>}
      </p>
      <h3 className="mt-5 font-display text-3xl font-semibold leading-[1.05] tracking-[-0.03em] text-[#ECEBE4] md:text-4xl">{r.q}</h3>
      <p className="mt-5 max-w-md text-base leading-relaxed text-[#9DA3AE]">{r.body}</p>
      <a href={r.href} className="group mt-7 inline-flex items-center gap-2 border-b border-white/30 pb-1 font-mono text-xs uppercase tracking-[0.16em] text-[#ECEBE4] transition-colors hover:border-[#BACD1C] hover:text-[#BACD1C]">
        {r.cta} <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </a>
    </Reveal>
    <Reveal delay={0.08} className={`min-w-0 ${flip ? "lg:order-1" : ""}`}>
      {r.img ? <Shot src={r.img} alt={r.q} /> : (
        <div className="overflow-hidden border border-white/[0.09] bg-[#0B0C0F]">
          {r.ticker && (
            <div className="h-[42px] overflow-hidden truncate whitespace-nowrap border-b border-white/[0.06] px-5 py-3 font-mono text-[11px] text-[#FFB224]" data-testid="news-typewriter">
              <Typewriter words={HEADLINES} typingSpeed={30} deleteSpeed={12} pause={2200} cursorClassName="text-[#FFB224]" />
            </div>
          )}
          <div className={r.ticker ? "" : "p-4 sm:p-6"}>{r.el}</div>
        </div>
      )}
    </Reveal>
  </div>
);

export const ModuleRows = () => (
  <section id="terminal" data-testid="module-rows" className="bg-[#0A0B0D] pt-24 md:pt-32">
    <div className="mx-auto max-w-[1400px] px-6 pb-8 md:px-12">
      <Reveal className="max-w-3xl pb-14">
        <Eyebrow>The full terminal</Eyebrow>
        <H2 className="mt-5">Five questions every trader asks. <span className="text-[#7D8390]">One terminal that answers them.</span></H2>
      </Reveal>
      {ROWS.map((r, i) => <Row key={r.k} r={r} flip={i % 2 === 1} />)}
    </div>
  </section>
);
